/**
 * Utility functions for parsing AI-generated text containing location markers
 * and get location data with descriptions
 */

/**
 * Parse AI text to get full location descriptions with bullet points
 * Captures the detailed explanation that appears before each [ADD_TO_MAP] marker
 *
 */
export const parseNotesFromAiText = (text) => {
	if (!text) return [];

	const notes = [];

	// Pattern to find [ADD_TO_MAP:lat,lng:score:note] markers
	// and _id prefix: [ADD_TO_MAP:_id:507f1f77bcf86cd799439011:lat,lng:score:note]
	const markerRegex = /\[ADD_TO_MAP:(?:_id:([a-f0-9]{24}):)?([-\d.]+),([-\d.]+):([^:]+):([^\]]+)\]/gi;

	let match;
	let lastIndex = 0;

	while ((match = markerRegex.exec(text)) !== null) {
		const locationId = match[1] || null;
		const lat = parseFloat(match[2]);
		const lng = parseFloat(match[3]);

		const markerStartIndex = match.index;

		// Get text BEFORE this marker (from last marker or start of text)
		const textBeforeMarker = text.substring(lastIndex, markerStartIndex);

		// Find the section header for this location
		// Pattern: **Best for [Factor] - [Address]** or **Strong [Factor] Option - [Address]**
		const headerPattern = /\*\*(?:Best for|Strong)\s+[^-]+-\s+[^\*]+\*\*/g;
		const headerMatches = [...textBeforeMarker.matchAll(headerPattern)];

		// Get the last header match (closest to this marker)
		const lastHeaderMatch = headerMatches[headerMatches.length - 1];

		if (lastHeaderMatch) {
			const headerStartIndex = lastHeaderMatch.index;
			// Get everything from the header to the marker
			const fullDescription = textBeforeMarker.substring(headerStartIndex).trim();

			// Clean up the description by removing markdown bold markers
			const cleanedDescription = fullDescription.replace(/\*\*/g, "").trim();

			if (lat && lng && cleanedDescription) {
				notes.push({
					_id: locationId,
					lat: Math.round(lat * 100) / 100,
					lng: Math.round(lng * 100) / 100,
					note: cleanedDescription,
				});
			}
		}

		lastIndex = match.index + match[0].length;
	}

	return notes;
};

/**
 * Extract location objects from saved AI content
 * Used for AI Replay hydration - extracts only coordinates and IDs without descriptions
 *
 */
export const extractLocationsFromSavedAiContent = (text) => {
	if (!text) return [];

	const locations = [];

	// Pattern matches all marker formats with optional score and note fields
	const markerRegex = /\[ADD_TO_MAP:(?:_id:([a-f0-9]{24}):)?([-\d.]+),([-\d.]+)(?::[^:\]]+)?(?::[^\]]+)?\]/gi;

	let match;

	while ((match = markerRegex.exec(text)) !== null) {
		const locationId = match[1] || null;
		const lat = parseFloat(match[2]);
		const lng = parseFloat(match[3]);

		// Skip invalid coordinates
		if (!lat || !lng) continue;

		locations.push({
			_id: locationId,
			location_id: locationId,
			lat,
			lng,
		});
	}

	return locations;
};

/**
 * Merge notes from AI text into location objects
 * Matches locations by ID first, then falls back to coordinate matching
 *
 */
export const mergeNotesIntoLocations = (locations, notesData) => {
	if (!locations || locations.length === 0) return locations;
	if (!notesData || notesData.length === 0) return locations;

	return locations.map((loc) => {
		// Round coordinates for matching
		const locLatRounded = Math.round(loc.lat * 100) / 100;
		const locLngRounded = Math.round(loc.lng * 100) / 100;

		// Find matching note by ID
		const matchingNote =
			notesData.find(
				(noteData) => noteData._id && (noteData._id === loc._id || noteData._id === loc.location_id),
			) ||
			// Fall back to coordinate matching
			notesData.find((noteData) => noteData.lat === locLatRounded && noteData.lng === locLngRounded);

		// Return location with note added if found
		return matchingNote ? { ...loc, note: matchingNote.note } : loc;
	});
};
